import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../../../components/layout/Navbar';
import Footer from '../../../components/layout/Footer';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { ChevronLeft, ChevronRight, Download, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLearnAuth } from '@/hooks/useLearnAuth';
import { markModuleComplete, getModuleProgress } from '@/utils/progressTracking';
import QuizPlayer from '../../../components/learn/QuizPlayer';
import ReactMarkdown from 'react-markdown';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { toast } from 'sonner';

interface Module {
  id: string;
  title: string;
  slug: string;
  level: string;
  module_order: number;
  duration_minutes: number;
  content: {
    video_url?: string;
    sections?: Array<{ title: string; content: string }>;
    learning_objectives?: string[];
    resources?: Array<{ title: string; type: string; url: string }>;
  };
}

interface Quiz {
  id: string;
  module_id: string;
  pass_mark: number;
  questions: any;
}

const LearnModulePlayer: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useLearnAuth();
  const [module, setModule] = useState<Module | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [progress, setProgress] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [allModules, setAllModules] = useState<Module[]>([]);

  useEffect(() => {
    if (!slug || !user) return;

    const fetchModuleData = async () => {
      try {
        // Fetch module
        const { data: moduleData, error: moduleError } = await supabase
          .from('modules')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();

        if (moduleError) throw moduleError;
        if (!moduleData) {
          toast.error('Module not found');
          navigate('/learn/app/modules');
          return;
        }

        setModule(moduleData as any);

        // Fetch quiz
        const { data: quizData } = await supabase
          .from('quizzes')
          .select('*')
          .eq('module_id', moduleData.id)
          .maybeSingle();

        setQuiz(quizData as any);

        // Fetch progress
        const progressData = await getModuleProgress(user.id, moduleData.id);
        setProgress(progressData);

        // Fetch all modules for navigation
        const { data: modulesData } = await supabase
          .from('modules')
          .select('*')
          .order('module_order');

        setAllModules((modulesData as any) || []);
      } catch (error) {
        console.error('Error fetching module:', error);
        toast.error('Failed to load module');
      } finally {
        setLoading(false);
      }
    };

    fetchModuleData();
  }, [slug, user, navigate]);

  const handleMarkComplete = async () => {
    if (!user || !module) return;

    try {
      await markModuleComplete(user.id, module.id);
      toast.success('Module marked as complete!');
      const updated = await getModuleProgress(user.id, module.id);
      setProgress(updated);
    } catch (error) {
      console.error('Error marking complete:', error);
      toast.error('Failed to mark module as complete');
    }
  };

  const handleQuizComplete = async (passed: boolean, score: number) => {
    if (passed) {
      await handleMarkComplete();
    }
    setShowQuiz(false);
  };

  const getPreviousModule = () => {
    if (!module) return null;
    const currentIndex = allModules.findIndex(m => m.id === module.id);
    return currentIndex > 0 ? allModules[currentIndex - 1] : null;
  };

  const getNextModule = () => {
    if (!module) return null;
    const currentIndex = allModules.findIndex(m => m.id === module.id);
    return currentIndex < allModules.length - 1 ? allModules[currentIndex + 1] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!module) {
    return null;
  }

  const previousModule = getPreviousModule();
  const nextModule = getNextModule();

  if (showQuiz && quiz && user && module) {
    return (
      <QuizPlayer
        quizId={quiz.id}
        moduleId={module.id}
        userId={user.id}
        onComplete={handleQuizComplete}
      />
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-5xl">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link to="/learn/app/modules">
              <Button variant="ghost" size="sm">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Modules
              </Button>
            </Link>
          </div>

          {/* Video Player */}
          {module.content.video_url && (
            <Card className="mb-8 overflow-hidden">
              <div className="aspect-video">
                <iframe
                  src={module.content.video_url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </Card>
          )}

          {/* Module Info */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Card className="p-6 mb-6">
                <div className="flex items-start justify-between mb-4">
                  <h1 className="text-2xl font-bold text-brand-navy">
                    Module {module.module_order}: {module.title}
                  </h1>
                  {progress?.completed && (
                    <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                  )}
                </div>

                {/* Sections */}
                {module.content.sections?.map((section, index) => (
                  <div key={index} className="mb-6">
                    <h2 className="text-xl font-semibold mb-3 text-brand-navy">{section.title}</h2>
                    <div className="prose prose-sm max-w-none text-gray-700">
                      <ReactMarkdown>{section.content}</ReactMarkdown>
                    </div>
                  </div>
                ))}

                {/* Learning Objectives */}
                {module.content.learning_objectives && module.content.learning_objectives.length > 0 && (
                  <>
                    <h2 className="text-xl font-semibold mb-3 text-brand-navy">Learning Objectives</h2>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                      {module.content.learning_objectives.map((objective, index) => (
                        <li key={index}>{objective}</li>
                      ))}
                    </ul>
                  </>
                )}

                {/* Actions */}
                <div className="space-y-3">
                  {quiz && (
                    <Button className="w-full" onClick={() => setShowQuiz(true)}>
                      {progress?.quiz_score ? `Retake Quiz (Previous: ${progress.quiz_score}%)` : 'Take Quiz (80% to pass)'}
                    </Button>
                  )}
                  {!progress?.completed && (
                    <Button variant="outline" className="w-full" onClick={handleMarkComplete}>
                      Mark as Complete
                    </Button>
                  )}
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div>
              {/* Downloads */}
              {module.content.resources && module.content.resources.length > 0 && (
                <Card className="p-6 mb-6">
                  <h3 className="font-semibold mb-4 text-brand-navy">Downloads</h3>
                  <div className="space-y-3">
                    {module.content.resources.map((resource, index) => (
                      <a key={index} href={resource.url} target="_blank" rel="noopener noreferrer">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {resource.title}
                        </Button>
                      </a>
                    ))}
                  </div>
                </Card>
              )}

              {/* Navigation */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4 text-brand-navy">Navigation</h3>
                <div className="space-y-3">
                  {previousModule ? (
                    <Link to={`/learn/app/modules/${previousModule.slug}`}>
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Previous Module
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" size="sm" className="w-full justify-start" disabled>
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Previous Module
                    </Button>
                  )}
                  {nextModule ? (
                    <Link to={`/learn/app/modules/${nextModule.slug}`}>
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        Next Module
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" size="sm" className="w-full justify-start" disabled>
                      Next Module
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default LearnModulePlayer;
